/**
 * Column picker: show / hide and reorder every archive column (drag a row, or
 * the ↑ ↓ buttons / Alt+Arrow keys), reset to the default set, and choose row
 * density. Saved per logged-in user (see `useColumnPrefs`).
 *
 * Columns that are visible by preference but do not fit the current width are
 * marked "no room" — they are still one tap away in the row expander.
 *
 * The picker itself is shared (`components/column-picker.tsx`); this wrapper
 * maps archive columns onto it with Archive's trigger styling.
 */
import { useMemo } from 'react';

import { ColumnPicker as SharedColumnPicker } from '../../components/column-picker';

import { COLUMN_GROUP_LABELS, type ArchiveColumn } from './archive-columns';
import { defaultPrefs, type ColumnPrefs } from './archive-layout';

export interface ColumnPickerProps {
  columns: readonly ArchiveColumn[];
  prefs: ColumnPrefs;
  onChange: (next: ColumnPrefs) => void;
  /** Ids visible by preference but dropped for width right now. */
  hiddenByWidth: ReadonlySet<string>;
  isSavedLocallyOnly: boolean;
  /** Controlled open state (the table's "+N more" opens the picker too). */
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function ColumnPicker({ columns, ...rest }: ColumnPickerProps): JSX.Element {
  const pickerColumns = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        title: column.title,
        groupLabel: COLUMN_GROUP_LABELS[column.group],
        isPinned: column.isPinned,
      })),
    [columns]
  );
  const defaults = useMemo(() => defaultPrefs(columns), [columns]);
  return <SharedColumnPicker columns={pickerColumns} defaults={defaults} {...rest} />;
}
